import styled, { createGlobalStyle, keyframes } from "styled-components";
import COLORS from "../../../assets/Colors";

export const theme = {
  teal: "#00838c",
  tealDark: "#006d75",
  tealDeep: "#005f66",
  tealLight: "#e6f5f6",
  tealGlow: "rgba(0,131,140,0.15)",
  navy: "#293461",
  navyDeep: "#1a2247",
  navyLight: "#3d4a7a",
  muted: "#949ab0",
  mutedLight: "#c5c9dc",
  white: "#ffffff",
  offWhite: "#f6f8ff",
  softWhite: "#f0f3fc",
  gold: "#d4a853",
  goldLight: "rgba(212,168,83,0.14)",
  error: "#e53e3e",
  errorLight: "#fff5f5",
};

export const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const pulseDot = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 0 rgba(0,131,140,0.5); }
  50%       { opacity: 0.7; transform: scale(0.82); box-shadow: 0 0 0 6px rgba(0,131,140,0); }
`;

export const rotateSlow = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

export const floatAnim = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-12px); }
`;

export const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ─── GLOBAL ───────────────────────────────────────────────────────────────────
export const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html { scroll-behavior: smooth; font-size: 16px; }
  body {
    font-family: openSans-Regular, sans-serif;
    color: ${theme.navy};
    background: ${theme.offWhite};
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }
  input, select, textarea, button { font-family: inherit; }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: ${theme.offWhite}; }
  ::-webkit-scrollbar-thumb { background: ${theme.teal}; border-radius: 3px; }
`;

// ─── SHARED ───────────────────────────────────────────────────────────────────
export const RevealWrapper = styled.div`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? "0" : "28px")});
  transition:
    opacity 0.75s ease,
    transform 0.75s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const SectionContainer = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  width: 100%;
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
  @media (max-width: 320px) {
    padding: 0 0.75rem;
  }
`;

export const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.7rem;
  font-family: openSans-SemiBold, sans-serif;
  color: ${({ $light }) => ($light ? "rgba(255,255,255,0.65)" : theme.teal)};
  letter-spacing: 2.5px;
  text-transform: uppercase;
  margin-bottom: 0.9rem;

  &::before {
    content: "";
    width: 26px;
    height: 2px;
    background: ${({ $light }) =>
      $light ? "rgba(255,255,255,0.45)" : theme.teal};
    border-radius: 1px;
    flex-shrink: 0;
  }
  @media (max-width: 480px) {
    font-size: 0.65rem;
  }
`;

export const SectionTitle = styled.h2`
  font-family: openSans-SemiBold, sans-serif;
  font-size: clamp(1.85rem, 4vw, 2.6rem);
  font-weight: 800;
  color: ${({ $light }) => ($light ? theme.white : theme.navy)};
  margin-bottom: 1rem;
  line-height: 1.18;
  letter-spacing: -0.5px;
`;

export const SectionSubtitle = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: clamp(0.95rem, 2vw, 1.05rem);
  color: ${({ $light }) => ($light ? "rgba(255,255,255,0.62)" : theme.muted)};
  line-height: 1.82;
  max-width: 580px;
  margin-bottom: 3rem;
  @media (max-width: 480px) {
    margin-bottom: 2rem;
  }
`;

// ─── NAVBAR ───────────────────────────────────────────────────────────────────
export const NavWrapper = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 clamp(1rem, 4vw, 2.5rem);
  background: #ffffff;
  backdrop-filter: blur(20px);
  border-bottom: 1px solid
    ${({ $scrolled }) => ($scrolled ? "rgba(0,131,140,0.12)" : "transparent")};
  box-shadow: ${({ $scrolled }) =>
    $scrolled ? "0 4px 28px rgba(41,52,97,0.08)" : "none"};
  transition: all 0.35s ease;
  @media (max-width: 480px) {
    height: 62px;
  }
`;

export const NavLogoLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-decoration: none;
`;

export const NavLogoBox = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${theme.navy} 0%, ${theme.teal} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px ${theme.tealGlow};
  flex-shrink: 0;
`;

export const NavLogoText = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 1.15rem;
  font-weight: 800;
  color: ${theme.navy};
  letter-spacing: -0.3px;
  span {
    color: ${theme.teal};
  }
`;

export const NavTagline = styled.div`
  font-size: 0.48rem;
  color: ${theme.muted};
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-top: -2px;
`;

export const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLink = styled.a`
  text-decoration: none;
  font-size: 0.86rem;
  font-family: openSans-SemiBold, sans-serif;
  color: ${COLORS.COLOR_DARK};
  opacity: 0.68;
  transition: opacity 0.2s;
  &:hover {
    opacity: 1;
  }
`;

export const NavCta = styled.a`
  padding: 0.55rem 1.4rem;
  border-radius: 50px;
  background: ${COLORS.BTN_GREEN};
  color: #ffffff;
  text-decoration: none;
  font-size: 0.85rem;
  font-family: openSans-SemiBold, sans-serif;
  font-weight: 700;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 16px ${theme.tealGlow};
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 131, 140, 0.35);
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

export const HamburgerBtn = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.4rem;
  color: ${theme.navy};
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const MobileMenu = styled.div`
  position: fixed;
  top: 68px;
  left: 0;
  right: 0;
  background: rgba(246, 248, 255, 0.98);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 131, 140, 0.1);
  padding: 1.5rem clamp(1rem, 4vw, 2.5rem);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  z-index: 999;
  animation: ${fadeInUp} 0.2s ease;
  @media (max-width: 480px) {
    top: 62px;
  }
`;

export const MobileNavLink = styled.a`
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.navy};
  padding: 0.7rem 0;
  border-bottom: 1px solid rgba(41, 52, 97, 0.06);
`;

export const MobileNavCta = styled.a`
  text-decoration: none;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  text-align: center;
  padding: 0.9rem;
  border-radius: 12px;
  background: linear-gradient(135deg, ${theme.teal}, ${theme.tealDark});
  box-shadow: 0 4px 16px ${theme.tealGlow};
  margin-top: 0.75rem;
`;

// ─── HERO ─────────────────────────────────────────────────────────────────────
export const HeroWrapper = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  padding: clamp(88px, 12vw, 120px) clamp(1rem, 4vw, 3rem) 5rem;
  background: linear-gradient(135deg, #f6f8ff 0%, #eef3ff 45%, #e8f7f8 100%);
  overflow: hidden;
`;

export const HeroBgDecorations = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
`;

export const HeroBgGrid = styled.div`
  position: absolute;
  inset: 0;
  background-size: 60px 60px;
  @media (max-width: 480px) {
    background-size: 38px 38px;
  }
`;

export const HeroBgBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  pointer-events: none;
`;

export const HeroRing = styled.div`
  position: absolute;
  border-radius: 50%;
  border: 1.5px solid rgba(0, 131, 140, 0.1);
`;

export const HeroDiamond = styled.div`
  position: absolute;
  bottom: 18%;
  left: 3%;
  width: clamp(90px, 13vw, 180px);
  height: clamp(90px, 13vw, 180px);
  border: 1.5px solid rgba(41, 52, 97, 0.07);
  transform: rotate(45deg);
`;

export const HeroContainer = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: clamp(2rem, 5vw, 5rem);
  align-items: start;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const HeroContent = styled.div`
  animation: ${fadeInUp} 0.85s ease-out;
`;

export const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 1.1rem;
  border-radius: 50px;
  background: ${theme.teal};
  color: #fff;
  font-size: 0.72rem;
  font-family: openSans-SemiBold, sans-serif;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 1.8rem;
  box-shadow: 0 4px 18px ${theme.tealGlow};
`;

export const PulseDot = styled.span`
  width: 8px;
  height: 8px;
  background: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: ${pulseDot} 2.2s ease-in-out infinite;
`;

export const HeroTitle = styled.h1`
  font-family: openSans-SemiBold, sans-serif;
  font-size: clamp(2rem, 5.5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.14;
  color: ${theme.navy};
  margin-bottom: 1.5rem;
  letter-spacing: -1px;
`;

export const HeroTitleAccent = styled.span`
  display: block;
  background: linear-gradient(135deg, ${theme.teal} 0%, ${theme.tealDark} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const HeroSubtitle = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  color: ${theme.muted};
  line-height: 1.82;
  max-width: 530px;
  margin-bottom: 2rem;
  @media (max-width: 960px) {
    max-width: 100%;
  }
`;

export const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-bottom: 2.2rem;
  @media (max-width: 960px) {
    justify-content: flex-start;
  }
`;

export const HeroMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 1rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 131, 140, 0.1);
  font-size: 0.86rem;
  font-weight: 600;
  color: ${theme.navy};
  box-shadow: 0 2px 8px rgba(41, 52, 97, 0.06);
  strong {
    color: ${theme.navy};
  }
`;

export const HeroCtaGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2.5rem;
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

export const BtnPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.88rem 2rem;
  border-radius: 12px;
  background: linear-gradient(135deg, ${theme.teal}, ${theme.tealDark});
  color: #fff;
  text-decoration: none;
  font-family: openSans-SemiBold, sans-serif;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.3px;
  box-shadow: 0 6px 22px rgba(0, 131, 140, 0.34);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.14),
      transparent
    );
    transition: left 0.55s ease;
  }
  &:hover::before {
    left: 100%;
  }
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 131, 140, 0.44);
  }
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

export const BtnSecondary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.88rem 2rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(10px);
  color: ${theme.navy};
  text-decoration: none;
  font-family: openSans-SemiBold, sans-serif;
  font-weight: 700;
  font-size: 0.95rem;
  border: 1.5px solid rgba(41, 52, 97, 0.12);
  transition: all 0.3s ease;
  &:hover {
    border-color: ${theme.teal};
    color: ${theme.teal};
    background: ${theme.tealLight};
  }
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

// ─── COUNTDOWN ────────────────────────────────────────────────────────────────
export const CountdownWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  flex-wrap: wrap;
  padding: 1rem 1.5rem;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    ${theme.navy} 0%,
    ${theme.navyLight} 100%
  );
  box-shadow: 0 8px 32px rgba(41, 52, 97, 0.22);
`;

export const CountdownLabel = styled.span`
  font-size: 0.68rem;
  font-family: openSans-SemiBold, sans-serif;
  color: #ffffff;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const CountdownBoxes = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const CountdownBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 52px;
`;

export const CountdownNum = styled.span`
  font-family: openSans-SemiBold, sans-serif;
  font-size: clamp(1.4rem, 4vw, 1.85rem);
  font-weight: 800;
  color: #ffffff;
  line-height: 1;
  letter-spacing: -1px;
`;

export const CountdownUnit = styled.span`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.6rem;
  color: ${COLORS.PRIMARY_WHITE};
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-top: 3px;
`;

// ─── REGISTRATION CARD ────────────────────────────────────────────────────────
export const CardWrapper = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  position: relative;
  overflow: hidden;
  box-shadow:
    0 24px 80px rgba(41, 52, 97, 0.12),
    0 4px 16px rgba(41, 52, 97, 0.06);
  border: 1px solid rgba(0, 131, 140, 0.08);
  animation: ${fadeInUp} 0.9s ease-out 0.15s both;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${theme.teal}, ${theme.tealDark});
  }
`;

export const CardBadge = styled.div`
  font-size: 0.68rem;
  font-family: openSans-SemiBold, sans-serif;
  color: ${theme.teal};
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 0.45rem;
`;

export const CardTitle = styled.h2`
  font-family: openSans-SemiBold, sans-serif;
  font-size: clamp(1.3rem, 3vw, 1.7rem);
  font-weight: 800;
  color: ${theme.navy};
  margin-bottom: 0.3rem;
`;

export const CardSubtitle = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.88rem;
  color: ${theme.muted};
  line-height: 1.6;
  margin-bottom: 0.75rem;
`;

export const CardSeats = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.9rem;
  border-radius: 50px;
  background: ${theme.goldLight};
  color: #9a6c18;
  font-size: 0.72rem;
  font-family: openSans-SemiBold, sans-serif;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 1.5rem;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
  margin-bottom: 0.85rem;
  @media (max-width: 400px) {
    grid-template-columns: 1fr;
    gap: 0.6rem;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.32rem;
  margin-bottom: ${({ $noMargin }) => ($noMargin ? 0 : "0.85rem")};
`;

export const FormLabel = styled.label`
  font-size: 0.72rem;
  font-family: openSans-SemiBold, sans-serif;
  font-weight: 700;
  color: ${COLORS.INPUT_LABEL};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  opacity: 0.8;
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1.5px solid
    ${({ $hasError }) => ($hasError ? theme.error : "rgba(41,52,97,0.12)")};
  background: ${({ $hasError }) =>
    $hasError ? theme.errorLight : "rgba(246,248,255,0.7)"};
  color: ${theme.navy};
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.9rem;
  outline: none;
  transition: all 0.22s ease;
  &::placeholder {
    color: ${theme.mutedLight};
  }
  &:focus {
    border-color: ${theme.teal};
    background: #fff;
    box-shadow: 0 0 0 3px ${theme.tealGlow};
  }
`;

export const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem 2.2rem 0.75rem 1rem;
  border-radius: 10px;
  border: 1.5px solid
    ${({ $hasError }) => ($hasError ? theme.error : "rgba(41,52,97,0.12)")};
  background-color: ${({ $hasError }) =>
    $hasError ? theme.errorLight : "rgba(246,248,255,0.7)"};
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  appearance: none;
  color: ${({ $hasValue }) => ($hasValue ? theme.navy : theme.muted)};
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.9rem;
  outline: none;
  cursor: pointer;
  transition: all 0.22s ease;
  option {
    color: ${theme.navy};
    background: #fff;
  }
  &:focus {
    border-color: ${theme.teal};
    background-color: #fff;
    box-shadow: 0 0 0 3px ${theme.tealGlow};
  }
`;

export const FieldError = styled.span`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.7rem;
  color: ${theme.error};
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

export const SubmitButton = styled.button`
  width: 100%;
  padding: 0.95rem;
  border-radius: 12px;
  border: none;
  cursor: ${({ disabled }) => (disabled ? "wait" : "pointer")};
  background: ${({ disabled }) =>
    disabled
      ? theme.mutedLight
      : `linear-gradient(135deg, ${theme.teal}, ${theme.tealDark})`};
  color: #fff;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  margin-top: 0.5rem;
  box-shadow: ${({ disabled }) =>
    disabled ? "none" : "0 6px 22px rgba(0,131,140,0.34)"};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.12),
      transparent
    );
    transition: left 0.5s ease;
  }
  &:not(:disabled):hover::before {
    left: 100%;
  }
  &:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(0, 131, 140, 0.44);
  }
`;

export const FormNote = styled.p`
  font-family: openSans-Regular, sans-serif;
  text-align: center;
  font-size: 0.73rem;
  color: rgba(41, 52, 97, 0.38);
  margin-top: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
`;

export const SuccessWrapper = styled.div`
  text-align: center;
  padding: 1rem 0;
`;

export const SuccessIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.teal}, ${theme.tealDark});
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  box-shadow: 0 8px 24px ${theme.tealGlow};
  animation: ${fadeInUp} 0.5s ease;
`;

export const SuccessTitle = styled.h3`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 1.4rem;
  font-weight: 800;
  color: ${theme.navy};
  margin-bottom: 0.7rem;
`;

export const SuccessText = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.9rem;
  color: ${theme.muted};
  line-height: 1.7;
  margin-bottom: 1.4rem;
`;

export const SuccessBadge = styled.div`
  padding: 0.85rem 1.2rem;
  border-radius: 12px;
  background: ${theme.tealLight};
  color: ${theme.teal};
  font-size: 0.88rem;
  font-weight: 600;
  font-family: openSans-SemiBold, sans-serif;
`;

// ─── SPEAKERS ─────────────────────────────────────────────────────────────────
export const SpeakersWrapper = styled.section`
  padding: clamp(4rem, 8vw, 7rem) clamp(1rem, 4vw, 3rem);
  background: #fff;
  position: relative;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
  }
`;

export const SpeakersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 720px;
  align-items: stretch;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    max-width: 100%;
  }
`;

export const SpeakerCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 22px;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(0, 131, 140, 0.08);
  box-shadow: 0 4px 20px rgba(41, 52, 97, 0.07);
  transition: all 0.38s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;

  &:hover {
    transform: translateY(-7px);
    box-shadow: 0 20px 56px rgba(41, 52, 97, 0.14);
  }
`;

export const SpeakerImgWrapper = styled.div`
  position: relative;
  height: 380px;
  overflow: hidden;
  background: ${theme.softWhite};
  flex-shrink: 0;
`;

export const SpeakerInfo = styled.div`
  padding: 1.4rem 1.5rem 1.6rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const SpeakerDescription = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.82rem;
  color: ${theme.muted};
  line-height: 1.65;
  margin-top: 0.55rem;
  flex: 1;
`;

export const SpeakerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  ${SpeakerCardWrapper}:hover & {
    transform: scale(1.05);
  }
`;

export const SpeakerImgOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(41, 52, 97, 0.88) 0%,
    transparent 58%
  );
`;

export const SpeakerTagBadge = styled.div`
  position: absolute;
  bottom: 1.1rem;
  left: 1.1rem;
  padding: 0.3rem 0.9rem;
  border-radius: 50px;
  background: ${theme.teal};
  color: #fff;
  font-size: 0.66rem;
  font-family: openSans-SemiBold, sans-serif;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

export const SpeakerName = styled.h3`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 1.1rem;
  font-weight: 800;
  color: ${theme.navy};
  margin-bottom: 0.28rem;
`;

export const SpeakerDesig = styled.p`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${theme.teal};
  margin-bottom: 0.3rem;
  letter-spacing: 0.3px;
`;

export const SpeakerOrg = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.8rem;
  color: ${theme.muted};
  line-height: 1.55;
`;

// ─── AGENDA ───────────────────────────────────────────────────────────────────
export const AgendaWrapper = styled.section`
  padding: clamp(4rem, 8vw, 7rem) clamp(1rem, 4vw, 3rem);
  background: linear-gradient(135deg, ${theme.navy} 0%, ${theme.navyDeep} 100%);
  position: relative;
  overflow: hidden;
`;

export const AgendaBgBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  &.b1 {
    top: 8%;
    right: 4%;
    width: 300px;
    height: 300px;
    background: radial-gradient(
      circle,
      rgba(0, 131, 140, 0.16) 0%,
      transparent 70%
    );
    filter: blur(30px);
  }
  &.b2 {
    bottom: 8%;
    left: 3%;
    width: 200px;
    height: 200px;
    background: radial-gradient(
      circle,
      rgba(212, 168, 83, 0.08) 0%,
      transparent 70%
    );
    filter: blur(24px);
  }
`;

export const AgendaContent = styled.div`
  position: relative;
  z-index: 2;
`;

export const TimelineWrapper = styled.div`
  position: relative;
  padding-left: 3rem;
  &::before {
    content: "";
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(
      to bottom,
      ${theme.teal} 0%,
      rgba(0, 131, 140, 0.18) 100%
    );
    border-radius: 2px;
  }
  @media (max-width: 480px) {
    padding-left: 2.2rem;
  }
`;

export const AgendaItemWrapper = styled.div`
  position: relative;
  padding-bottom: 2.5rem;
  cursor: default;
  &::before {
    content: "";
    position: absolute;
    left: -3rem;
    top: 0.15rem;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ $hovered }) => ($hovered ? theme.teal : theme.navy)};
    border: 3px solid
      ${({ $hovered }) => ($hovered ? theme.teal : theme.mutedLight)};
    box-shadow: ${({ $hovered }) =>
      $hovered ? `0 0 0 5px ${theme.tealGlow}` : "none"};
    transition: all 0.3s ease;
    z-index: 2;
  }
  @media (max-width: 480px) {
    &::before {
      left: -2.2rem;
    }
  }
`;

export const AgendaTime = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  color: ${theme.teal};
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 0.4rem;
`;

export const AgendaTitle = styled.h3`
  font-family: openSans-SemiBold, sans-serif;
  font-size: clamp(1rem, 2.5vw, 1.15rem);
  font-weight: 700;
  color: ${({ $hovered }) => ($hovered ? theme.teal : "#fff")};
  margin-bottom: 0.38rem;
  transition: color 0.3s ease;
`;

export const AgendaDesc = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.87rem;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.72;
  max-width: 520px;
`;

// ─── HIGHLIGHTS ───────────────────────────────────────────────────────────────
export const HighlightsWrapper = styled.section`
  padding: clamp(4rem, 8vw, 7rem) clamp(1rem, 4vw, 3rem);
  background: ${theme.softWhite};
`;

export const HighlightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
  gap: 1.4rem;
  align-items: stretch; /* Important */

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const HighlightCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  border-radius: 20px;
  background: ${({ $hovered }) => ($hovered ? theme.tealLight : "#fff")};
  border: 1.5px solid
    ${({ $hovered }) =>
      $hovered ? "rgba(0,131,140,0.2)" : "rgba(41,52,97,0.06)"};
  box-shadow: ${({ $hovered }) =>
    $hovered
      ? "0 12px 40px rgba(0,131,140,0.1)"
      : "0 2px 10px rgba(41,52,97,0.05)"};
  height: 100%; /* Important */
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const HighlightIconBox = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 13px;
  background: ${theme.tealLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  margin-bottom: 1.1rem;
  border: 1.5px solid
    ${({ $hovered }) => ($hovered ? "rgba(0,131,140,0.22)" : "transparent")};
  transition: border-color 0.3s ease;
`;

export const HighlightTitle = styled.h3`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${theme.navy};
  margin-bottom: 0.48rem;
`;

export const HighlightText = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.86rem;
  color: ${theme.muted};
  line-height: 1.72;
  flex: 1;
  margin-top: auto;
`;

// ─── CTA ─────────────────────────────────────────────────────────────────────
export const CtaWrapper = styled.section`
  padding: clamp(4rem, 8vw, 7rem) clamp(1rem, 4vw, 3rem);
  text-align: center;
  background: linear-gradient(
    135deg,
    ${theme.teal} 0%,
    ${theme.tealDark} 50%,
    ${theme.navy} 100%
  );
  position: relative;
  overflow: hidden;
`;

export const CtaBgGrid = styled.div`
  position: absolute;
  inset: 0;
  background-size: 50px 50px;
  pointer-events: none;
`;

export const CtaRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  pointer-events: none;
`;

export const CtaContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 660px;
  margin: 0 auto;
  p {
    font-family: openSans-Regular, sans-serif;
    color: rgba(255, 255, 255, 0.72);
    font-size: clamp(0.95rem, 2vw, 1.06rem);
    line-height: 1.82;
    margin-bottom: 2.5rem;
  }
`;

export const CtaTitle = styled.h2`
  font-family: openSans-SemiBold, sans-serif;
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 1.1rem;
  letter-spacing: -0.8px;
`;

export const BtnCtaWhite = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 1rem 2.5rem;
  border-radius: 14px;
  background: #ffffff;
  color: ${theme.teal};
  text-decoration: none;
  font-family: openSans-SemiBold, sans-serif;
  font-weight: 800;
  font-size: 1.05rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 44px rgba(0, 0, 0, 0.24);
  }
`;

export const CtaTrustRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 2.5rem;
  flex-wrap: wrap;
`;

export const CtaTrustItem = styled.div`
  font-size: 0.82rem;
  font-family: openSans-SemiBold, sans-serif;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
`;

// ─── FOOTER ───────────────────────────────────────────────────────────────────
export const FooterWrapper = styled.footer`
  padding: 2rem clamp(1rem, 4vw, 3rem);
  background: ${theme.navyDeep};
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

export const FooterInner = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
  }
`;

export const FooterBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
`;

export const FooterLogoBox = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: linear-gradient(135deg, ${theme.navy}, ${theme.teal});
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const FooterBrandText = styled.span`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.98rem;
  font-weight: 700;
  color: #fff;
  span {
    color: ${theme.teal};
  }
`;

export const FooterCopy = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.3);
`;

export const FooterLinks = styled.div`
  display: flex;
  gap: 1.4rem;
`;

export const FooterLink = styled.a`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.4);
  text-decoration: none;
  transition: color 0.2s ease;
  &:hover {
    color: ${theme.teal};
  }
`;

export const LogoContainer = styled.div`
  display: flex;
  flex: 1;
  @media ${theme?.breakpoints?.sm_up} {
    margin-left: 0px;
  }
`;
